# THE SCREEN A CHILD SEES WHEN SOMETHING BREAKS — card 6ab166a3a4d698cda2b1fa90.
#
# Owner, 2026-09-21: "We should show a pretty screen or funny animation and
# then refresh... A clear and refresh button though is necessary because if its
# a bad login, we can have them clear localstorage and relogin."
#
# WHAT IT REPLACED. The boundary rendered `ErrorScreen` with three paragraphs
# of apology, the browser and OS, and "Show Error Details" opening onto a
# JavaScript stack — written for a grown-up reading diagnostics down the phone
# to support. The person in front of it is usually a child, and the screen just
# stopped. Everything it was asking them to transcribe is now on the CrashLog
# row the boundary files by itself (card 6aad7bb96e6a1e18a090353b).
#
# `ErrorScreen` ITSELF IS UNTOUCHED and still exported from common.coffee: the
# dashboard, the teacher portal and the folder list all use it as a plain
# message frame ("You do not currently have any students"), which has nothing
# to do with crashing. This is a second component for the one caller that is
# actually about a crash.
#
# THE ANIMATION IS CSS, NOT AN ASSET. A crash screen that has to fetch
# something is a crash screen that is blank exactly when the network is the
# thing that broke — which is the ChunkLoadError case. The logo is already
# loaded on every page of the shell; the rest is keyframes.

import React from 'react'
# Pure and import-free itself, so this screen still loads nothing it cannot.
import { darkLogoMedia } from './color_scheme_rules.coffee'

BOUNCE = 'elfeet-crash-bounce'
DOTS = 'elfeet-crash-dots'

# Injected once, from here, so the whole screen is one import with no stylesheet
# to keep in step. Guarded on the element id: the boundary can render more than
# once in a page life.
STYLE_ID = 'elfeet-crash-screen-style'
STYLE = """
@keyframes #{BOUNCE} {
  0%, 100% { transform: translateY(0) rotate(-2deg); }
  50%      { transform: translateY(-18px) rotate(2deg); }
}
@keyframes #{DOTS} {
  0%   { opacity: .2; }
  50%  { opacity: 1; }
  100% { opacity: .2; }
}
.elfeet-crash-logo { animation: #{BOUNCE} 1.6s ease-in-out infinite; }
.elfeet-crash-dot  { animation: #{DOTS} 1.2s ease-in-out infinite; }
.elfeet-crash-dot:nth-child(2) { animation-delay: .2s; }
.elfeet-crash-dot:nth-child(3) { animation-delay: .4s; }
@media (prefers-reduced-motion: reduce) {
  .elfeet-crash-logo, .elfeet-crash-dot { animation: none; }
}
"""

installStyle = ->
  try
    return false unless document?.getElementById?
    return false if document.getElementById STYLE_ID
    node = document.createElement 'style'
    node.id = STYLE_ID
    node.appendChild document.createTextNode STYLE
    document.head.appendChild node
    true
  catch e
    false

# WHAT THE SCREEN SAYS, BY WHAT IT IS ABOUT TO DO. A promise the screen cannot
# keep is worse than no promise: `stop` has already tried both moves, so it
# does not claim another one is coming.
HEADLINES =
  refresh: 'Oops! Our elephant tripped.'
  dashboard: 'Still wobbly — taking you somewhere safe.'
  stop: 'Our elephant needs a hand.'

SUBLINES =
  refresh: 'Hang on, we are picking things back up...'
  dashboard: 'Hang on, we are taking you back to the start...'
  # "one of these" was two buttons; there is one now (owner ruling 2026-09-22).
  stop: 'Try this, and we will keep working on it.'

BUTTON =
  background: '#337ab7'
  border: 'none'
  borderRadius: '6px'
  color: '#fff'
  cursor: 'pointer'
  fontSize: '16px'
  margin: '6px'
  padding: '10px 22px'


export default class CrashScreen extends React.PureComponent
  componentDidMount: -> installStyle()

  render: ->
    phase = @props.phase ? 'stop'
    headline = if @props.headline? then @props.headline else (HEADLINES[phase] ? HEADLINES.stop)
    subline = if @props.subline? then @props.subline else (SUBLINES[phase] ? SUBLINES.stop)
    working = phase isnt 'stop'

    <div className="loading_screen">
      <div className="loading_window error_window">
        <section className="centered">
          <picture>
            {<source srcSet="#{window.config?.s3_assets ? ''}images/logo-light-text.png" media={darkMedia} /> if darkMedia = darkLogoMedia()}
            <img
              className="elfeet-crash-logo"
              src="#{window.config?.s3_assets ? ''}images/logo-dark-text.png"
              alt="Elephant Learning"
            />
          </picture>
        </section>
        <hr/>
        <p className="loading_text centered" style={{fontSize: '22px', fontWeight: 'bold'}}>
          {headline}
        </p>
        <p className="loading_text centered">
          {subline}
          { if working
              <span>
                {' '}
                <span className="elfeet-crash-dot">.</span>
                <span className="elfeet-crash-dot">.</span>
                <span className="elfeet-crash-dot">.</span>
              </span>
          }
        </p>
        { ### WHETHER WE HAVE THE CRASH — card 6aadb69ed60dadb9b2befafa's honest
            sentence, handed down from the boundary. Only on the step where the
            screen stays put; while a refresh is coming, it is noise. ### }
        { if @props.note
            <p className="loading_text centered" style={{fontSize: '13px', opacity: 0.75}}>{@props.note}</p>
        }
        <p className="centered">
          { ### ONE BUTTON, AT EVERY STEP — owner ruling 2026-09-22, made in
              front of the live screen: "there is no need for refresh or any
              other button. So one button, log out and refresh, executes what
              clear and refresh does (empties everything out so they can start
              over) and other than that it is already refreshing on its own."

              The manual `Refresh now` earned nothing: the boundary already
              reloads by itself once the CrashLog report is acknowledged, so
              the button only did sooner what the screen was doing anyway — and
              it is the wrong move for the one crash a reload cannot fix. A bad
              login crashes again, and again, until somebody clears the stored
              session, which is what this button does: empty everything out and
              start over. Unconditional, because making a child watch two
              useless reloads before offering the way out is the thing this
              screen exists not to do. ### }
          <button type="button" style={BUTTON} onClick={@props.onClearAndRefresh}>
            Log Out and Refresh
          </button>
        </p>
      </div>
    </div>
